import styled from 'styled-components';

/* ── HERO ── */
export const ServerHero = styled.section`
  background: linear-gradient(180deg, #0e0c1a 0%, #1a1535 40%, #0e0c1a 100%);
  padding: 32px 24px 0;
  text-align: center;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse at 50% 0%, rgba(139,90,43,0.18) 0%, transparent 65%);
    pointer-events: none;
  }

  .hero-inner {
    max-width: 992px;
    margin: 0 auto;
    position: relative;
  }

  .server-name {
    font-size: 22px;
    font-weight: 800;
    color: #fff;
    line-height: 1.25;
    margin-bottom: 8px;

    .verified-icon {
      color: #27ae60;
      font-size: 18px;
      vertical-align: middle;
      margin-left: 6px;
    }
  }

  .server-tagline {
    font-size: 13px;
    color: #999;
    margin-bottom: 20px;
    line-height: 1.5;
  }

  .server-banner-img {
    width: 100%;
    height: 300px;
    object-fit: fill;
    border-radius: 8px;
    border: 1px solid rgba(255,255,255,0.1);
    display: block;
    margin: 0 auto;
    box-shadow: 0 8px 32px rgba(0,0,0,0.5);
  }
`;

/* ── STATS BAR ── */
export const StatsBar = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 24px;

  .stats-row {
    display: flex;
    align-items: center;
    background: #1A1A1A;
    border: 1px solid rgba(255,255,255,0.06);
    border-top: none;
    border-radius: 0 0 8px 8px;
    overflow: hidden;
  }

  .stat-item {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    border-inline-end: 1px solid rgba(255,255,255,0.06);

    &:last-child { border-inline-end: none; }

    .stat-icon {
      color: #6b7280;
      font-size: 14px;
      flex-shrink: 0;
    }

    .stat-body {
      .stat-label {
        font-size: 9px;
        color: #4b5563;
        text-transform: uppercase;
        letter-spacing: 0.07em;
      }

      .stat-value {
        font-size: 13px;
        font-weight: 700;
        color: #e2e8f0;
        display: flex;
        align-items: center;
        gap: 4px;

        .lang-flags {
          display: flex;
          gap: 2px;
          font-size: 13px;
        }
      }
    }
  }
`;

/* ── PAGE LAYOUT ── */
export const DetailsLayout = styled.div`
  max-width: 1100px;
  margin: 24px auto 0;
  padding: 0 24px;
  display: grid;
  grid-template-columns: 1fr 260px;
  gap: 20px;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    .details-sidebar { display: none; }
  }
`;

/* ── DESCRIPTION ── */
export const DescriptionBox = styled.div`
  background: #1A1A1A;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 8px;
  padding: 20px 22px;
  font-size: 13px;
  color: #9ca3af;
  line-height: 1.75;

  h3 {
    font-size: 13px;
    font-weight: 700;
    color: #e2e8f0;
    margin: 14px 0 6px;

    &:first-child { margin-top: 0; }
  }

  p { margin-bottom: 10px; }

  ul {
    padding-left: 18px;
    margin-bottom: 10px;
    list-style: disc;

    li {
      margin-bottom: 4px;
      color: #9ca3af;
    }
  }

  .signature {
    margin-top: 16px;
    font-style: italic;
    color: #6b7280;
    font-size: 12px;
  }
`;

/* ── SIDEBAR ── */
export const DetailsSidebar = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 10px;

  .rank-card {
    background: linear-gradient(135deg, rgba(180,130,30,0.25) 0%, rgba(120,80,10,0.2) 100%);
    border: 1px solid rgba(255,215,0,0.3);
    border-radius: 8px;
    padding: 14px 16px;
    text-align: center;

    .rank-label {
      font-size: 9px;
      font-weight: 700;
      color: #c8a040;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      margin-bottom: 2px;
    }

    .rank-number {
      font-size: 36px;
      font-weight: 900;
      color: #ffd700;
      line-height: 1;
      text-shadow: 0 0 20px rgba(255,215,0,0.4);
    }
  }

  .vote-card {
    background: #1A1A1A;
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 8px;
    padding: 16px;
    display: flex;
    align-items: center;
    gap: 12px;
    cursor: pointer;
    transition: border-color 0.2s, background 0.2s;

    &:hover {
      border-color: rgba(147,51,234,0.4);
      background: rgba(147,51,234,0.08);
    }

    .vote-icon {
      color: #27ae60;
      font-size: 28px;
    }

    .vote-count {
      font-size: 28px;
      font-weight: 800;
      color: #27ae60;
      font-variant-numeric: tabular-nums;
    }

    &.voted {
      border-color: rgba(124,58,237,0.5);
      background: rgba(124,58,237,0.1);
      cursor: default;
      &:hover { border-color: rgba(124,58,237,0.5); background: rgba(124,58,237,0.1); }
      .vote-icon, .vote-count { color: #a78bfa; }
    }

    &.locked {
      cursor: not-allowed;
      opacity: 0.45;
      &:hover { border-color: rgba(255,255,255,0.06); background: #1A1A1A; }
      .vote-icon, .vote-count { color: #6b7280; }
    }
  }

  .action-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    border: none;
    transition: opacity 0.2s;
    text-decoration: none;
    width: 100%;

    &:hover { opacity: 0.85; }

    &.website-btn {
      background: rgba(255,255,255,0.05);
      color: #e2e8f0;
      border: 1px solid rgba(255,255,255,0.06);
    }

    &.discord-btn {
      background: #5865f2;
      color: #fff;
    }
  }

  .new-launches-widget {
    background: #1A1A1A;
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 8px;
    overflow: hidden;

    .widget-header {
      background: rgba(147,51,234,0.08);
      border-bottom: 1px solid rgba(147,51,234,0.15);
      padding: 8px 12px;
      font-size: 9px;
      font-weight: 700;
      color: #a78bfa;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      display: flex;
      align-items: center;
      gap: 6px;

      .dot { width: 7px; height: 7px; border-radius: 50%; background: #27ae60; }
    }

    .launch-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 12px;
      border-bottom: 1px solid rgba(255,255,255,0.04);
      transition: background 0.15s;
      cursor: pointer;

      &:last-child { border-bottom: none; }
      &:hover { background: rgba(255,255,255,0.03); }

      .launch-avatar {
        width: 26px;
        height: 26px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
        font-weight: 700;
        color: #fff;
        flex-shrink: 0;
      }

      .launch-info {
        flex: 1;
        min-width: 0;

        .launch-name {
          font-size: 11px;
          color: #e2e8f0;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 4px;

          .online-dot {
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background: #27ae60;
            flex-shrink: 0;
          }
        }

        .launch-meta {
          font-size: 9px;
          color: #4b5563;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }

    .upcoming-label {
      padding: 6px 12px;
      font-size: 9px;
      color: #6b7280;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      border-top: 1px solid rgba(255,255,255,0.04);
    }
  }
`;

/* ── COMMENTS SECTION ── */
export const CommentsSection = styled.section`
  max-width: 1100px;
  width: 100%;
  /* width: 1100px; */
  margin: 24px auto 0;
  padding: 0 24px;

  .section-title {
    font-size: 16px;
    font-weight: 700;
    color: #f1f5f9;
    margin-bottom: 16px;
    padding-bottom: 10px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
  }

  .review-form {
    background: #1A1A1A;
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 8px;
    padding: 18px 20px;
    margin-bottom: 20px;

    .form-label {
      font-size: 13px;
      color: #9ca3af;
      margin-bottom: 10px;
      display: block;
    }

    .star-row {
      display: flex;
      gap: 4px;
      margin-bottom: 14px;

      .star {
        font-size: 22px;
        cursor: pointer;
        color: #4b5563;
        transition: color 0.15s;

        &.filled { color: #ffd700; }
        &:hover { color: #ffd700; }
      }
    }

    .review-textarea {
      width: 100%;
      background: rgba(255,255,255,0.04);
      border: 1px solid rgba(255,255,255,0.06);
      border-radius: 6px;
      padding: 10px 14px;
      font-size: 12px;
      color: #e2e8f0;
      resize: none;
      min-height: 80px;
      outline: none;
      font-family: inherit;
      transition: border-color 0.2s;

      &::placeholder { color: #4b5563; }
      &:focus { border-color: rgba(109,40,217,0.5); }
    }

    .submit-btn {
      margin-top: 10px;
      padding: 9px 20px;
      background: #9333EA;
      border: none;
      border-radius: 6px;
      color: #fff;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.2s;

      &:hover { background: #7c22d4; }
    }
  }

  .comments-list {
    display: flex;
    flex-direction: column;
    gap: 0;
    background: #1A1A1A;
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 8px;
    overflow: hidden;
  }

  .comment-card {
    padding: 14px 18px;
    border-bottom: 1px solid rgba(255,255,255,0.05);
    display: flex;
    gap: 12px;

    &:last-child { border-bottom: none; }
    &:hover { background: rgba(255,255,255,0.02); }

    .comment-avatar {
      width: 34px;
      height: 34px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 13px;
      font-weight: 700;
      color: #fff;
      flex-shrink: 0;
    }

    .comment-body {
      flex: 1;
      min-width: 0;

      .comment-header {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
        margin-bottom: 4px;

        .comment-author {
          font-size: 13px;
          font-weight: 700;
          color: #e2e8f0;
        }

        .comment-stars {
          display: flex;
          gap: 2px;
          font-size: 11px;
          color: #ffd700;
        }

        .comment-date {
          font-size: 11px;
          color: #4b5563;
          margin-inline-start: auto;
        }

        .comment-age-badge {
          font-size: 9px;
          font-weight: 800;
          padding: 1px 7px;
          border-radius: 4px;
          color: #fff;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          flex-shrink: 0;

          &.cbadge-new   { background: linear-gradient(135deg, #ef4444, #f97316); }
          &.cbadge-today { background: #9333EA; }
          &.cbadge-week  { background: #3b82f6; }
        }
      }

      .comment-text {
        font-size: 13px;
        color: #9ca3af;
        line-height: 1.5;
      }

      .comment-actions {
        display: flex;
        align-items: center;
        gap: 14px;
        margin-top: 8px;

        .like-btn {
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 12px;
          color: #6b7280;
          cursor: pointer;
          background: none;
          border: none;
          padding: 0;
          transition: color 0.2s;

          &:hover { color: #a78bfa; }
        }

        .report-btn {
          font-size: 12px;
          color: #4b5563;
          cursor: pointer;
          background: none;
          border: none;
          padding: 0;
          transition: color 0.2s;
          display: flex;
          align-items: center;
          gap: 4px;

          &:hover { color: #e74c3c; }
        }
      }
    }
  }

  .comments-pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    margin-top: 16px;
    padding-bottom: 8px;

    button {
      min-width: 32px;
      height: 32px;
      padding: 0 8px;
      border-radius: 4px;
      border: 1px solid rgba(255,255,255,0.06);
      background: #1A1A1A;
      color: #9ca3af;
      font-size: 13px;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        border-color: rgba(109,40,217,0.4);
        color: #9333EA;
      }

      &.active {
        background: rgba(147,51,234,0.15);
        border-color: rgba(147,51,234,0.5);
        color: #a78bfa;
        font-weight: 700;
      }
    }
  }
`;
